import type {
  AccountView,
  BossId,
  ContentResponse,
  LevelView,
  ProgressResponse,
  RoomMode,
  Settings,
  ShopResponse,
} from '@bioforge/protocol';
import type { Container } from 'pixi.js';
import type { ApiClient } from './api/api-client.js';
import type { ConnectionIndicator } from './overlays/connection-indicator.js';
import type { SeenTracker } from './story/story.js';

/** Экран игры. Навигация между экранами — внутри приложения, без параметров в адресе. */
export interface Scene {
  readonly view: Container;
  /** Снимает подписки (клавиатура, сокет, DOM) и освобождает графику. */
  destroy(): void;
}

/** Как попасть в сетевую комнату. */
export type OnlineIntent =
  | { readonly kind: 'create'; readonly mode: RoomMode; readonly bossId?: BossId }
  /** Вход по коду. Если это своя комната из сессии (перезагрузка страницы) — место восстанавливается. */
  | { readonly kind: 'join'; readonly code: string };

/**
 * Данные с сервера, общие для экранов (SPEC.md, раздел 17): аккаунт, контент, прогресс,
 * отметки «уже видел» и настройки.
 * Экран, который меняет данные (вход, результат уровня), обновляет их здесь.
 */
export interface AppContext {
  readonly api: ApiClient;
  /** Состояние соединения: экран может показать его сам (бейдж на главном экране). */
  readonly connection: ConnectionIndicator;
  account: AccountView;
  content: ContentResponse;
  progress: ProgressResponse;
  seen: SeenTracker;
  settings: Settings;
  /** Магазин: каталог, свои предметы и надетые скины. */
  shop: ShopResponse;
}

export interface Navigator {
  menu(notice?: string): void;
  expedition(notice?: string): void;
  /** Энциклопедия и «Архив лаборатории»; закрытие возвращает в главное меню. */
  encyclopedia(): void;
  /** Магазин без оплаты (SPEC.md, раздел 18); закрытие возвращает в главное меню. */
  shop(): void;
  /** Начинает уровень: новая сцена и карточки знакомства, затем попытка с сервера и партия. */
  play(level: LevelView): void;
  /** Сцена после победы в уровне, если игрок её ещё не видел. */
  celebrate(level: LevelView): void;
  online(intent: OnlineIntent): void;
}

/** Подписка на событие окна, которую экран снимает при закрытии. */
export function listen<K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
): () => void {
  window.addEventListener(type, handler);
  return () => window.removeEventListener(type, handler);
}
